import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../../../api/supabase'
import { useAuth } from '../../../contexts/AuthContext'
import { hasAnyRole } from '../../../lib/roles'
import WarehouseLayout from '../WarehouseLayout'
import { inputCls, btnSecondary, fmtDateTime } from '../types'
import type { DeliveryNote } from '../types'
import { useStickyHScroll } from '../../../hooks/useStickyHScroll'
import { StickyHScrollbar } from '../../../components/StickyHScrollbar'
import { classifyOverdue, daysBetween, OVERDUE_ROW_CLASS, OVERDUE_TEXT_CLASS } from '../../../lib/overdue'
import { NoteSourceLink, useNoteSources } from '../NoteSourceLink'

// Ops/customer-service board (Gary, 2026-09-24, merged single-note model 2026-09-28) -- only
// notes whose fulfilment_type is COLLECTION_AND_DELIVERY ever appear here (COLLECTION_ONLY
// notes are done the moment they're collected, straight to Outbound). lifecycle_status='PLANNED'
// covers both PENDING (not yet collected) and IN_TRANSIT (collected, not yet delivered) --
// transit_status distinguishes the two on the same row. DELIVERED flips lifecycle_status to
// COMPLETED (Gary's decision B) and the note leaves this board. Outbound is a separate line: its
// record was already written at Confirm Collected (left the warehouse); confirming delivery only
// flips that same record from "In transit (awaiting POD)" to "Completed ✓" -- it adds nothing new.

// No hard number was specified for the transit-time warning -- these are reasonable UK
// domestic pallet/FTL defaults, easy to tune later if they don't match reality.
const WARN_DAYS = 3
const ALERT_DAYS = 7

export default function DeliveriesPage() {
  const { id } = useParams<{ id: string }>()
  const { user, userRoles } = useAuth()
  const canConfirmDelivered = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])

  const [notes, setNotes] = useState<DeliveryNote[]>([])
  const noteSources = useNoteSources(notes)
  const [loading, setLoading] = useState(true)
  const [toast, setToast] = useState<string | null>(null)
  const [filterJob, setFilterJob] = useState('')

  const [deliverNote, setDeliverNote] = useState<DeliveryNote | null>(null)
  const [deliverForm, setDeliverForm] = useState({ at: '', receiver: '', pod: false, notes: '' })
  const [submitting, setSubmitting] = useState(false)

  const fetchNotes = useCallback(async () => {
    setLoading(true)
    const { data } = await supabase.from('delivery_notes')
      .select('*, devannings!inner(warehouse_id)')
      .eq('devannings.warehouse_id', id!)
      .eq('fulfilment_type', 'COLLECTION_AND_DELIVERY')
      .eq('status', 'ACTIVE').eq('lifecycle_status', 'PLANNED')
      .order('scheduled_date', { ascending: true })
    setNotes((data as DeliveryNote[] | null) || [])
    setLoading(false)
  }, [id])

  useEffect(() => { fetchNotes() }, [fetchNotes])

  function showToast(msg: string) {
    setToast(msg)
    setTimeout(() => setToast(null), 3500)
  }

  const visible = notes
    .filter(n => !filterJob || n.job_ref.toLowerCase().includes(filterJob.toLowerCase()) || String(n.serial_no).includes(filterJob))
    .slice()
    .sort((a, b) => {
      const rank = (n: DeliveryNote) => n.transit_status === 'IN_TRANSIT' ? 0 : 1 // in-transit first, needs attention
      return rank(a) - rank(b) || a.scheduled_date.localeCompare(b.scheduled_date)
    })

  const { scrollProps: notesScrollProps, contentRef: notesTableRef, bar: notesScrollBar } = useStickyHScroll([visible])

  const pendingCount = visible.filter(n => n.transit_status === 'PENDING').length
  const inTransitCount = visible.filter(n => n.transit_status === 'IN_TRANSIT').length
  const overdueCount = visible.filter(n =>
    n.transit_status === 'IN_TRANSIT' && n.collected_at && daysBetween(n.collected_at, new Date().toISOString()) > WARN_DAYS
  ).length

  function openDeliver(note: DeliveryNote) {
    setDeliverNote(note)
    setDeliverForm({ at: new Date().toISOString().slice(0, 16), receiver: note.actual_receiver_name || '', pod: false, notes: '' })
  }
  async function submitDeliver() {
    if (!deliverNote) return
    setSubmitting(true)
    try {
      const deliveredAt = deliverForm.at ? new Date(deliverForm.at).toISOString() : new Date().toISOString()
      await supabase.from('delivery_notes').update({
        transit_status: 'DELIVERED',
        lifecycle_status: 'COMPLETED', // Gary's decision B -- POD confirmation is completion
        delivered_at: deliveredAt,
        delivered_by: user?.id || null,
        // completed_at stays the moment the goods LEFT the warehouse (= collected_at); the arrival
        // time lives in delivered_at. Two different moments, recorded separately.
        completed_at: deliverNote.collected_at || deliveredAt,
        completed_by: deliverNote.collected_by || user?.id || null,
        actual_receiver_name: deliverForm.receiver || null,
        pod_received: deliverForm.pod,
        completion_notes: deliverForm.notes || null,
      }).eq('id', deliverNote.id)
      setDeliverNote(null)
      await fetchNotes()
      showToast(`#${deliverNote.serial_no} delivered ✓`)
    } catch (err: any) {
      alert(`Error: ${err.message}`)
    } finally { setSubmitting(false) }
  }

  return (
    <WarehouseLayout warehouseId={id!}>
      <div className="space-y-4">
        {toast && (
          <div className="fixed top-4 right-4 z-50 bg-green-600 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg">✓ {toast}</div>
        )}

        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Deliveries</h2>
          <button onClick={fetchNotes} className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">Refresh</button>
        </div>

        {!canConfirmDelivered && (
          <p className="text-xs text-gray-400">Read-only -- confirming delivered requires the OPERATIONS or ADMIN role.</p>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <input className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 text-xs bg-white dark:bg-gray-700 dark:text-white w-48"
            value={filterJob} onChange={e => setFilterJob(e.target.value)} placeholder="Search job ref / serial no…" />
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide mb-1">Awaiting Collection</p>
            <p className="text-3xl font-bold text-gray-500 dark:text-gray-400">{pendingCount}</p>
            <p className="text-xs text-gray-400 mt-0.5">pushed, not left the warehouse yet (confirm on Planned Outbound)</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-semibold tracking-wide mb-1">In Transit</p>
            <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">{inTransitCount}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-amber-300 dark:border-amber-700 px-4 py-3">
            <p className="text-xs text-amber-600 dark:text-amber-400 uppercase font-semibold tracking-wide mb-1">⚠️ Over {WARN_DAYS}d in transit</p>
            <p className="text-3xl font-bold text-amber-600 dark:text-amber-400">{overdueCount}</p>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto" {...notesScrollProps}>
            {loading ? (
              <div className="py-16 text-center text-gray-400">Loading…</div>
            ) : visible.length === 0 ? (
              <div className="py-16 text-center text-gray-400">No pending or in-transit deliveries.</div>
            ) : (
              <table ref={notesTableRef as React.RefObject<HTMLTableElement>} className="min-w-full text-sm divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    {['STATUS', 'COLLECTED', 'IN TRANSIT', 'JOB REF', 'SERIAL NO', 'DELIVER TO', 'RELEASED TO', 'PLTS', 'CTNS', ''].map(h => (
                      <th key={h} className="px-3 py-2.5 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {visible.map(note => {
                    const transitDays = note.transit_status === 'IN_TRANSIT' && note.collected_at ? daysBetween(note.collected_at, new Date().toISOString()) : null
                    const transitLevel = classifyOverdue(transitDays, { warnAtDays: WARN_DAYS + 1, dangerAtDays: ALERT_DAYS + 1 })
                    const transitCls = transitDays == null ? 'text-gray-400' : OVERDUE_TEXT_CLASS[transitLevel]
                    const missingDestination = !note.deliver_to_name
                    return (
                      <tr key={note.id} className={transitLevel !== 'ok' ? OVERDUE_ROW_CLASS[transitLevel] : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'}>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${note.transit_status === 'IN_TRANSIT' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                            {note.transit_status === 'IN_TRANSIT' ? '🚚 In Transit' : '⏳ Awaiting collection'}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{note.collected_at ? fmtDateTime(note.collected_at) : '—'}</td>
                        <td className={`px-3 py-2.5 text-xs whitespace-nowrap ${transitCls}`}>{transitDays != null ? `${transitDays}d` : '—'}</td>
                        <td className="px-3 py-2.5 font-mono text-xs text-blue-700 dark:text-blue-400 font-semibold whitespace-nowrap">{note.job_ref}
                        <NoteSourceLink note={note} source={noteSources[note.id]} warehouseId={id!} backLabel="Deliveries" />
                      </td>
                        <td className="px-3 py-2.5 font-mono text-xs">#{note.serial_no}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 max-w-[180px] truncate">
                          {missingDestination ? (
                            <Link to={`/warehouse/${id}/devannings/${note.devanning_id}/notes`} className="text-amber-600 dark:text-amber-400 underline">
                              ⚠️ Needs Deliver To address
                            </Link>
                          ) : note.deliver_to_name}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">{note.release_to_name || '—'}</td>
                        <td className="px-3 py-2.5 text-xs font-semibold font-mono text-center text-purple-700 dark:text-purple-400">{note.pallets_qty}</td>
                        <td className="px-3 py-2.5 text-xs font-semibold font-mono text-center text-blue-700 dark:text-blue-400">{note.nop}</td>
                        <td className="px-3 py-2.5 whitespace-nowrap">
                          {note.transit_status === 'IN_TRANSIT' && canConfirmDelivered && (
                            <button onClick={() => openDeliver(note)}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded-md hover:bg-green-700 font-medium whitespace-nowrap">Confirm Delivered</button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
        <StickyHScrollbar {...notesScrollBar} />
      </div>

      {/* Confirm Delivered dialog */}
      {deliverNote && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
            <div className="px-6 pt-5 pb-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 dark:text-white">Confirm Delivered — #{deliverNote.serial_no}</h3>
              <button onClick={() => setDeliverNote(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Actual Delivered Date/Time</label>
                <input type="datetime-local" className={inputCls} value={deliverForm.at} onChange={e => setDeliverForm(f => ({ ...f, at: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Receiver Name (optional)</label>
                <input className={inputCls} value={deliverForm.receiver} onChange={e => setDeliverForm(f => ({ ...f, receiver: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Notes (optional)</label>
                <textarea rows={2} className={inputCls} value={deliverForm.notes} onChange={e => setDeliverForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <label className="flex items-center gap-2 text-xs font-medium text-gray-700 dark:text-gray-300">
                <input type="checkbox" checked={deliverForm.pod} onChange={e => setDeliverForm(f => ({ ...f, pod: e.target.checked }))} />
                POD received (proof of delivery on file)
              </label>
            </div>
            <div className="px-6 pb-5 flex gap-3 justify-end border-t border-gray-200 dark:border-gray-700 pt-3">
              <button onClick={() => setDeliverNote(null)} className={btnSecondary}>Cancel</button>
              <button onClick={submitDeliver} disabled={submitting}
                className="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {submitting ? 'Saving…' : 'Confirm Delivered'}
              </button>
            </div>
          </div>
        </div>
      )}
    </WarehouseLayout>
  )
}
